import toast from "react-hot-toast";
import ToastNotification from "./ToastNotification";
import { AxiosError } from "axios";
import { useCallback } from "react";

type ErrorDataType = {
  status: boolean;
  message: any;
  data: any;
};

export const useAPIUtils = () => {
  const extractErrorMessage = useCallback((rawMessage: any): string => {
    if (typeof rawMessage === "string") return rawMessage;

    if (Array.isArray(rawMessage)) {
      const flattened = rawMessage.flat?.() ?? rawMessage;
      const joined = flattened.map((m) => (typeof m === "string" ? m : JSON.stringify(m))).join(", ");
      if (joined) return joined;
    }

    if (rawMessage && typeof rawMessage === "object") {
      const values = Object.values(rawMessage).flat?.() ?? Object.values(rawMessage);
      const stringified = values.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join(", ");
      if (stringified) return stringified;
    }

    if (rawMessage) return String(rawMessage);
    return "Something Went Wrong.";
  }, []);

  const captureError = useCallback(
    (error: AxiosError, capture500: boolean = true) => {
      if (error?.response?.status === 413) return;
      const errorResponse = error?.response?.data as ErrorDataType | undefined;
      const message = extractErrorMessage(errorResponse?.message ?? error.message);

      if (error?.response?.status === 400 || error.code === "ERR_BAD_REQUEST") {
        toast.custom((t) => <ToastNotification message={message} isError={true} t={t} />, {
          id: "custom-toast",
          duration: 5000,
        });
      }
      if (error?.response?.status === 500) {
        capture500 &&
          toast.custom(
            (t) => (
              <ToastNotification message={`Server responded 500 for : ${error?.config?.url}`} isError={true} t={t} />
            ),
            {
              id: "custom-toast",
              duration: 5000,
            },
          );
      }
    },
    [extractErrorMessage],
  );

  const toastNotification = useCallback((message: string) => {
    toast.custom((t) => <ToastNotification message={message} isError={false} t={t} />, {
      id: "custom-toast",
      duration: 4000,
    });
  }, []);

  const toastError = (message: string) => {
    toast.custom((t) => <ToastNotification message={message} isError={true} t={t} />, {
      id: "custom-toast",
      duration: 5000,
    });
  };

  return {
    captureError,
    extractErrorMessage,
    toastNotification,
    toastError,
  };
};
